/**
 * @module index
 * @package @geenius/adapters/cloudflareKV
 * @description Implements the launch Cloudflare Workers KV provider contract
 * as a key/value DB adapter without importing Workers runtime SDKs.
 */

import type {
  DbAdapter,
  ListOptions,
  QueryCondition,
  QueryFilter,
} from "@geenius/adapters";
import {
  AdapterConfigurationError,
  AdapterError,
  DbError,
} from "@geenius/adapters";

type KvRecord = Record<string, unknown> & { id: string };
type CloudflareKvUnsupportedOperation = "join" | "transaction" | "sql";

/**
 * Domain key parts addressed by the Cloudflare KV adapter's key builders.
 * Exported so consumers writing custom Workers KV inspection tooling can
 * reuse the same domain vocabulary the adapter validates against.
 */
export type CloudflareKvKeyPart = "collection" | "field" | "id" | "keyPrefix";

/**
 * Builds the collection-index key the adapter writes under
 * `{keyPrefix}:{collection}:__index`. Each part is URI-encoded so user-provided
 * collection names cannot break the `:` delimiter convention.
 *
 * Use this when reading or invalidating an index entry from outside the
 * adapter — never hand-roll the key string, since the encoding rules may
 * tighten over time.
 *
 * @param keyPrefix Domain prefix configured on `createCloudflareKvDbAdapter`
 * @param collection Logical collection name (URI-encoded into the key)
 */
export function buildCloudflareKvCollectionIndexKey(
  keyPrefix: string,
  collection: string,
): string {
  return `${encodeOperationKeyPart(keyPrefix, "keyPrefix")}:${encodeOperationKeyPart(
    collection,
    "collection",
  )}:__index`;
}

/**
 * Builds the `namespace.list({ prefix })` value used to scan every record key
 * for a collection. Produces `{keyPrefix}:{collection}:` (trailing colon
 * intentional — paired with `recordKey()` whose id segment follows).
 *
 * @param keyPrefix Domain prefix configured on `createCloudflareKvDbAdapter`
 * @param collection Logical collection name (URI-encoded into the prefix)
 */
export function buildCloudflareKvCollectionRecordPrefix(
  keyPrefix: string,
  collection: string,
): string {
  return `${encodeOperationKeyPart(keyPrefix, "keyPrefix")}:${encodeOperationKeyPart(
    collection,
    "collection",
  )}:`;
}

/**
 * Builds the fully-qualified record key written for a single document:
 * `{keyPrefix}:{collection}:{id}`. Each segment is URI-encoded.
 *
 * @param keyPrefix Domain prefix configured on `createCloudflareKvDbAdapter`
 * @param collection Logical collection name
 * @param id Record id
 */
export function buildCloudflareKvRecordKey(
  keyPrefix: string,
  collection: string,
  id: string,
): string {
  return `${encodeOperationKeyPart(keyPrefix, "keyPrefix")}:${encodeOperationKeyPart(
    collection,
    "collection",
  )}:${encodeOperationKeyPart(id, "id")}`;
}

/**
 * Key/value DB adapter surface implemented by the Cloudflare Workers KV
 * contract package.
 */
export interface CloudflareKvDbAdapter {
  count(collection: string, filter?: QueryFilter): Promise<number>;
  create<T extends Record<string, unknown>>(
    collection: string,
    data: Omit<T, "id">,
  ): Promise<T & { id: string }>;
  delete(collection: string, id: string): Promise<boolean>;
  get<T>(collection: string, id: string): Promise<T | null>;
  join(): Promise<never>;
  list<T>(collection: string, options?: ListOptions): Promise<T[]>;
  query<T>(collection: string, filter: QueryFilter): Promise<T[]>;
  sql(): Promise<never>;
  transaction(): Promise<never>;
  update<T extends Record<string, unknown>>(
    collection: string,
    id: string,
    data: Partial<T>,
  ): Promise<T | null>;
}

/** Options forwarded to Workers KV writes when record or index TTL is enabled. */
export interface CloudflareKvPutOptions {
  expirationTtl?: number;
}

/** Options forwarded to Workers KV prefix listing. */
export interface CloudflareKvListOptions {
  cursor?: string;
  limit?: number;
  prefix?: string;
}

/** Key metadata returned by Workers KV prefix listing. */
export interface CloudflareKvListKey {
  name: string;
}

/** Result returned by Workers KV prefix listing. */
export interface CloudflareKvListResult {
  cursor?: string;
  keys: CloudflareKvListKey[];
  list_complete: boolean;
}

/** Minimal Workers KV namespace contract consumed by the adapter factory. */
export interface CloudflareKvNamespace {
  get(key: string): Promise<string | null>;
  put(
    key: string,
    value: string,
    options?: CloudflareKvPutOptions,
  ): Promise<void>;
  delete(key: string): Promise<void>;
  list?(options?: CloudflareKvListOptions): Promise<CloudflareKvListResult>;
}

/** Configuration accepted by {@link createCloudflareKvDbAdapter}. */
export interface CloudflareKvDbAdapterOptions {
  namespace: CloudflareKvNamespace;
  /**
   * TTL applied to record payloads. Collection indexes persist by default so
   * record refreshes cannot make live rows unreachable from list/query/count.
   */
  defaultTtlSeconds?: number;
  idFactory?: () => string;
  /**
   * Optional TTL for collection indexes when the whole collection should expire.
   * Requires record TTL and must be greater than or equal to record TTL so
   * collection indexes cannot expire before live records.
   */
  indexTtlSeconds?: number;
  keyPrefix?: string;
  /**
   * Maximum record payloads that filtered query/count scans may read. KV is a
   * key/value backend, so larger filtered scans should move to Convex/Neon.
   */
  maxScanRecords?: number;
}

/**
 * Binding wrapper accepted by {@link withCloudflareKvDb} when downstream code
 * already constructed a DB adapter and wants Cloudflare KV contract validation.
 */
export interface CloudflareKvDbAdapterBinding {
  adapter: DbAdapter;
}

/**
 * Accepted input for binding an existing DB adapter or constructing a new
 * Cloudflare KV-backed adapter.
 */
export type CloudflareKvDbAdapterInput =
  | DbAdapter
  | CloudflareKvDbAdapterBinding
  | CloudflareKvDbAdapterOptions;

/** Error thrown when Cloudflare KV adapter configuration is invalid. */
export class CloudflareKvBindingError extends AdapterConfigurationError {
  readonly cloudflareCode = "INVALID_CLOUDFLARE_KV_ADAPTER";

  /**
   * Creates a typed Cloudflare KV binding error.
   *
   * @param message Human-readable validation failure.
   * @param cause Optional original error that triggered the failure.
   */
  constructor(message: string, cause?: unknown) {
    super(message, "INVALID_CONFIG", cause);
    this.name = "CloudflareKvBindingError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Error thrown when callers request relational operations unsupported by KV. */
export class AdapterUnsupportedError extends AdapterError {
  readonly name = "AdapterUnsupportedError";

  /**
   * Creates an unsupported-operation adapter error for the DB domain.
   *
   * @param message Human-readable unsupported-operation message.
   * @param cause Optional original error that triggered the failure.
   */
  constructor(message: string, cause?: unknown) {
    super(message, "db", "ADAPTER_UNSUPPORTED", cause);
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const adapterMethods = [
  "create",
  "get",
  "update",
  "delete",
  "list",
  "query",
  "count",
] as const;
const CLOUDFLARE_KV_MAX_VALUE_BYTES = 25 * 1024 * 1024;
const CLOUDFLARE_KV_MAX_KEY_BYTES = 512;
const DEFAULT_CLOUDFLARE_KV_MAX_SCAN_RECORDS = 1000;
const MIN_CLOUDFLARE_KV_TTL_SECONDS = 60;
const queryOperators = new Set<QueryCondition["operator"]>([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "contains",
]);

function defaultIdFactory(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `kv_${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function parseKvJson(raw: string, context: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch (error) {
    throw new DbError(
      `Invalid Cloudflare KV JSON payload for ${context}`,
      "QUERY_ERROR",
      error,
    );
  }
}

function stringifyKvJson(value: unknown, context: string): string {
  try {
    const payload = JSON.stringify(value);
    if (payload === undefined) {
      throw new TypeError("JSON.stringify returned undefined");
    }
    return payload;
  } catch (error) {
    throw new DbError(
      `Invalid Cloudflare KV JSON payload for ${context}`,
      "QUERY_ERROR",
      error,
    );
  }
}

function assertKvValueSize(value: string, context: string): void {
  const byteLength = new TextEncoder().encode(value).byteLength;

  if (byteLength > CLOUDFLARE_KV_MAX_VALUE_BYTES) {
    throw new DbError(
      `Cloudflare KV value for ${context} exceeds the 25 MiB value limit`,
      "QUERY_ERROR",
    );
  }
}

function assertKvKeySize(key: string, context: string): void {
  const byteLength = new TextEncoder().encode(key).byteLength;

  if (byteLength > CLOUDFLARE_KV_MAX_KEY_BYTES) {
    throw new DbError(
      `Cloudflare KV key for ${context} exceeds the 512 byte key limit`,
      "QUERY_ERROR",
    );
  }
}

function invalidJsonPayload(
  context: string,
  path: string,
  detail: string,
): never {
  throw new DbError(
    `Cloudflare KV ${context} payload must be a JSON-serializable record (${path}: ${detail})`,
    "QUERY_ERROR",
  );
}

function assertJsonValue(
  value: unknown,
  context: string,
  path: string,
  seen: WeakSet<object>,
): void {
  if (value === null) {
    return;
  }

  switch (typeof value) {
    case "string":
    case "boolean":
      return;
    case "number":
      if (!Number.isFinite(value)) {
        invalidJsonPayload(context, path, "expected a finite number");
      }
      return;
    case "object":
      break;
    default:
      invalidJsonPayload(context, path, "expected a JSON value");
  }

  if (seen.has(value)) {
    invalidJsonPayload(context, path, "cyclic reference");
  }
  seen.add(value);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      if (!(index in value)) {
        invalidJsonPayload(context, `${path}[${index}]`, "sparse array slot");
      }
      assertJsonValue(value[index], context, `${path}[${index}]`, seen);
    }
    seen.delete(value);
    return;
  }

  if (!isPlainRecord(value)) {
    invalidJsonPayload(context, path, "expected a plain object");
  }

  const symbolKeys = Object.getOwnPropertySymbols(value);
  if (symbolKeys.length > 0) {
    invalidJsonPayload(
      context,
      `${path}[${String(symbolKeys[0])}]`,
      "symbol-keyed fields are not JSON-serializable",
    );
  }

  for (const [key, child] of Object.entries(value)) {
    assertJsonValue(child, context, `${path}.${key}`, seen);
  }

  seen.delete(value);
}

function assertJsonRecordPayload(
  value: unknown,
  context: string,
): asserts value is Record<string, unknown> {
  if (!isPlainRecord(value)) {
    throw new DbError(
      `Cloudflare KV ${context} payload must be a plain JSON record`,
      "QUERY_ERROR",
    );
  }

  try {
    assertJsonValue(value, context, "$", new WeakSet<object>());
  } catch (error) {
    if (error instanceof DbError) {
      throw error;
    }
    throw new DbError(
      `Invalid Cloudflare KV JSON payload for ${context}`,
      "QUERY_ERROR",
      error,
    );
  }
}

function assertTtl(
  value: number | undefined,
  optionName: string,
): number | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!Number.isInteger(value) || value < MIN_CLOUDFLARE_KV_TTL_SECONDS) {
    throw new CloudflareKvBindingError(
      `${optionName} must be an integer number of seconds greater than or equal to ${MIN_CLOUDFLARE_KV_TTL_SECONDS}`,
    );
  }

  return value;
}

function assertBindingKeyPart(
  value: string,
  part: CloudflareKvKeyPart,
): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new CloudflareKvBindingError(
      `Cloudflare KV ${part} must be a non-empty string`,
    );
  }

  return value;
}

// Reserved record id literals collide with the collection-index sentinel key
// suffix `:__index`. Allowing them as record ids would cause `create()` to
// write a record at the same KV key the adapter uses for its collection index,
// silently overwriting the index on the next write. Reject before any KV op.
const RESERVED_RECORD_ID_VALUES = new Set<string>(["__index"]);

function assertOperationKeyPart(
  value: string,
  part: CloudflareKvKeyPart,
): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DbError(
      `Cloudflare KV ${part} must be a non-empty string`,
      "QUERY_ERROR",
    );
  }

  if (part === "id" && RESERVED_RECORD_ID_VALUES.has(value)) {
    throw new DbError(
      `Cloudflare KV id "${value}" is reserved for the collection index`,
      "QUERY_ERROR",
    );
  }

  return value;
}

function encodeBindingKeyPart(
  value: string,
  part: CloudflareKvKeyPart,
): string {
  return encodeURIComponent(assertBindingKeyPart(value, part));
}

function encodeOperationKeyPart(
  value: string,
  part: CloudflareKvKeyPart,
): string {
  return encodeURIComponent(assertOperationKeyPart(value, part));
}

function assertOperationNonNegativeInteger(
  value: number | undefined,
  optionName: string,
): void {
  if (value === undefined) {
    return;
  }

  if (!Number.isInteger(value) || value < 0) {
    throw new DbError(
      `${optionName} must be a non-negative integer`,
      "QUERY_ERROR",
    );
  }
}

function assertListOptions(options: ListOptions | undefined): void {
  if (!options) {
    return;
  }

  assertOperationNonNegativeInteger(options.limit, "list limit");
  assertOperationNonNegativeInteger(options.offset, "list offset");

  if (options.orderBy !== undefined) {
    assertOperationKeyPart(options.orderBy, "field");
  }

  if (
    options.order !== undefined &&
    options.order !== "asc" &&
    options.order !== "desc"
  ) {
    throw new DbError(
      'list order must be either "asc" or "desc"',
      "QUERY_ERROR",
    );
  }
}

function assertListResult(
  value: unknown,
  collection: string,
): CloudflareKvListResult {
  if (
    !isRecord(value) ||
    !Array.isArray(value.keys) ||
    typeof value.list_complete !== "boolean" ||
    (value.cursor !== undefined && typeof value.cursor !== "string")
  ) {
    throw new DbError(
      `Invalid Cloudflare KV list page for ${collection}`,
      "QUERY_ERROR",
    );
  }

  if (
    !value.keys.every((key) => isRecord(key) && typeof key.name === "string")
  ) {
    throw new DbError(
      `Invalid Cloudflare KV list key for ${collection}`,
      "QUERY_ERROR",
    );
  }

  return value as unknown as CloudflareKvListResult;
}

function isComparableQueryValue(value: unknown): value is number | string {
  return typeof value === "number" || typeof value === "string";
}

function assertQueryConditionOperand(
  condition: QueryCondition,
  methodName: string,
  index: number,
): void {
  switch (condition.operator) {
    case "eq":
    case "neq":
      return;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      if (!isComparableQueryValue(condition.value)) {
        throw new DbError(
          `${methodName} filter condition ${index} requires a string or number value`,
          "QUERY_ERROR",
        );
      }
      return;
    case "in":
      if (!Array.isArray(condition.value)) {
        throw new DbError(
          `${methodName} filter condition ${index} requires an array value`,
          "QUERY_ERROR",
        );
      }
      return;
    case "contains":
      if (typeof condition.value !== "string") {
        throw new DbError(
          `${methodName} filter condition ${index} requires a string value`,
          "QUERY_ERROR",
        );
      }
      return;
  }
}

function assertQueryFilter(
  filter: QueryFilter,
  methodName: string,
): QueryFilter {
  if (!Array.isArray(filter)) {
    throw new DbError(
      `${methodName} filter must be an array of query conditions`,
      "QUERY_ERROR",
    );
  }

  for (const [index, condition] of filter.entries()) {
    if (
      !isRecord(condition) ||
      typeof condition.field !== "string" ||
      condition.field.trim().length === 0 ||
      !queryOperators.has(condition.operator as QueryCondition["operator"])
    ) {
      throw new DbError(
        `${methodName} filter condition ${index} is invalid`,
        "QUERY_ERROR",
      );
    }
    assertQueryConditionOperand(condition as QueryCondition, methodName, index);
  }

  return filter;
}

function putOptionsForTtl(
  ttlSeconds: number | undefined,
): CloudflareKvPutOptions | undefined {
  return ttlSeconds === undefined ? undefined : { expirationTtl: ttlSeconds };
}

function assertIndexTtlConsistency(
  defaultTtlSeconds: number | undefined,
  indexTtlSeconds: number | undefined,
): void {
  if (indexTtlSeconds === undefined) {
    return;
  }

  if (defaultTtlSeconds === undefined) {
    throw new CloudflareKvBindingError(
      "indexTtlSeconds requires defaultTtlSeconds so collection indexes cannot expire before records",
    );
  }

  if (indexTtlSeconds < defaultTtlSeconds) {
    throw new CloudflareKvBindingError(
      "indexTtlSeconds must be greater than or equal to defaultTtlSeconds",
    );
  }
}

function assertMaxScanRecords(value: number | undefined): number {
  const maxScanRecords = value ?? DEFAULT_CLOUDFLARE_KV_MAX_SCAN_RECORDS;

  if (!Number.isInteger(maxScanRecords) || maxScanRecords < 0) {
    throw new CloudflareKvBindingError(
      "maxScanRecords must be a non-negative integer",
    );
  }

  return maxScanRecords;
}

function unsupportedRelationalOperation(
  operation: CloudflareKvUnsupportedOperation,
): never {
  throw new AdapterUnsupportedError(
    `Cloudflare KV does not support relational ${operation} operations`,
  );
}

function assertDbAdapter(value: unknown): asserts value is DbAdapter {
  if (!isRecord(value)) {
    throw new CloudflareKvBindingError(
      "Expected a DbAdapter object for Cloudflare KV binding",
    );
  }

  const missing = adapterMethods.filter(
    (method) => typeof value[method] !== "function",
  );
  if (missing.length > 0) {
    throw new CloudflareKvBindingError(
      `Cloudflare KV DbAdapter is missing methods: ${missing.join(", ")}`,
    );
  }
}

function assertNamespace(
  value: unknown,
): asserts value is CloudflareKvNamespace {
  if (
    !isRecord(value) ||
    typeof value.get !== "function" ||
    typeof value.put !== "function" ||
    typeof value.delete !== "function"
  ) {
    throw new CloudflareKvBindingError(
      "Cloudflare KV options must include a namespace with get, put, and delete",
    );
  }
  if (value.list !== undefined && typeof value.list !== "function") {
    throw new CloudflareKvBindingError(
      "Cloudflare KV namespace.list must be a function when provided",
    );
  }
}

function assertAdapterOptions(
  value: unknown,
): asserts value is CloudflareKvDbAdapterOptions {
  if (!isRecord(value)) {
    throw new CloudflareKvBindingError(
      "Cloudflare KV options must include a namespace with get, put, and delete",
    );
  }
}

function assertIdFactory(value: unknown): () => string {
  if (value === undefined) {
    return defaultIdFactory;
  }

  if (typeof value !== "function") {
    throw new CloudflareKvBindingError(
      "Cloudflare KV idFactory must be a function",
    );
  }

  return () => {
    try {
      return value() as string;
    } catch (error) {
      throw new DbError(
        "Cloudflare KV idFactory failed to generate an id",
        "QUERY_ERROR",
        error,
      );
    }
  };
}

function isCloudflareKvDbAdapterOptions(
  value: unknown,
): value is CloudflareKvDbAdapterOptions {
  return isRecord(value) && "namespace" in value;
}

function compareValues(
  left: unknown,
  right: unknown,
  operator: Extract<QueryCondition["operator"], "gt" | "gte" | "lt" | "lte">,
): boolean {
  if (typeof left === "number" && typeof right === "number") {
    if (operator === "gt") return left > right;
    if (operator === "gte") return left >= right;
    if (operator === "lt") return left < right;
    return left <= right;
  }

  if (typeof left === "string" && typeof right === "string") {
    if (operator === "gt") return left > right;
    if (operator === "gte") return left >= right;
    if (operator === "lt") return left < right;
    return left <= right;
  }

  return false;
}

function matchesCondition(item: KvRecord, condition: QueryCondition): boolean {
  const value = item[condition.field];

  switch (condition.operator) {
    case "eq":
      return value === condition.value;
    case "neq":
      return value !== condition.value;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return compareValues(value, condition.value, condition.operator);
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(value);
    case "contains":
      return (
        typeof value === "string" &&
        typeof condition.value === "string" &&
        value.includes(condition.value)
      );
    default:
      return false;
  }
}

function matchesFilter(item: KvRecord, filter: QueryFilter): boolean {
  return filter.every((condition: QueryCondition) =>
    matchesCondition(item, condition),
  );
}

function sortRecords(records: KvRecord[], options?: ListOptions): KvRecord[] {
  if (!options?.orderBy) {
    return records;
  }

  const direction = options.order === "desc" ? -1 : 1;
  return [...records].sort((left, right) => {
    const leftValue = left[options.orderBy as string];
    const rightValue = right[options.orderBy as string];
    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return leftValue < rightValue
        ? -direction
        : leftValue > rightValue
          ? direction
          : 0;
    }
    if (typeof leftValue === "string" && typeof rightValue === "string") {
      return leftValue < rightValue
        ? -direction
        : leftValue > rightValue
          ? direction
          : 0;
    }
    return 0;
  });
}

function pageRecords(records: KvRecord[], options?: ListOptions): KvRecord[] {
  const offset = options?.offset ?? 0;
  const limit = options?.limit;
  return typeof limit === "number"
    ? records.slice(offset, offset + limit)
    : records.slice(offset);
}

/**
 * Creates a Cloudflare Workers KV-backed DB adapter for key/value workloads.
 *
 * Workers KV is eventually consistent. Direct `get()` reads target record keys,
 * while `list()`, `query()`, and `count()` prefer Workers KV prefix listing
 * when the namespace exposes it and fall back to the collection index key for
 * minimal test namespaces. Use Convex or Neon for transactional or query-heavy
 * workloads.
 *
 * @param options KV namespace, key prefix, TTL, and id factory configuration.
 * @returns A DB adapter implementing CRUD, list, query, and count with KV storage.
 */
export function createCloudflareKvDbAdapter(
  options: CloudflareKvDbAdapterOptions,
): CloudflareKvDbAdapter {
  assertAdapterOptions(options);
  assertNamespace(options.namespace);

  const namespace = options.namespace;
  const keyPrefix = encodeBindingKeyPart(
    options.keyPrefix ?? "geenius:adapters",
    "keyPrefix",
  );
  const idFactory = assertIdFactory(options.idFactory);
  const defaultTtlSeconds = assertTtl(
    options.defaultTtlSeconds,
    "defaultTtlSeconds",
  );
  const indexTtlSeconds = assertTtl(options.indexTtlSeconds, "indexTtlSeconds");
  assertIndexTtlConsistency(defaultTtlSeconds, indexTtlSeconds);
  const maxScanRecords = assertMaxScanRecords(options.maxScanRecords);
  const recordPutOptions = putOptionsForTtl(defaultTtlSeconds);
  const indexPutOptions = putOptionsForTtl(indexTtlSeconds);
  const indexMutationLocks = new Map<string, Promise<void>>();
  const collectionKey = (collection: string): string =>
    `${keyPrefix}:${encodeOperationKeyPart(collection, "collection")}:__index`;
  const collectionRecordPrefix = (collection: string): string =>
    `${keyPrefix}:${encodeOperationKeyPart(collection, "collection")}:`;
  const recordKey = (collection: string, id: string): string =>
    `${keyPrefix}:${encodeOperationKeyPart(
      collection,
      "collection",
    )}:${encodeOperationKeyPart(id, "id")}`;

  const kvGet = async (
    key: string,
    context: string,
  ): Promise<string | null> => {
    assertKvKeySize(key, context);

    try {
      return await namespace.get(key);
    } catch (error) {
      throw new DbError(
        `Cloudflare KV read failed for ${context}`,
        "CONNECTION_ERROR",
        error,
      );
    }
  };

  const kvPut = async (
    key: string,
    value: string,
    context: string,
    putOptions?: CloudflareKvPutOptions,
  ): Promise<void> => {
    assertKvKeySize(key, context);
    assertKvValueSize(value, context);

    try {
      await namespace.put(key, value, putOptions);
    } catch (error) {
      throw new DbError(
        `Cloudflare KV write failed for ${context}`,
        "CONNECTION_ERROR",
        error,
      );
    }
  };

  const kvDelete = async (key: string, context: string): Promise<void> => {
    assertKvKeySize(key, context);

    try {
      await namespace.delete(key);
    } catch (error) {
      throw new DbError(
        `Cloudflare KV delete failed for ${context}`,
        "CONNECTION_ERROR",
        error,
      );
    }
  };

  const readIndex = async (collection: string): Promise<string[]> => {
    const raw = await kvGet(collectionKey(collection), `${collection} index`);
    if (!raw) {
      return [] as string[];
    }
    const parsed = parseKvJson(raw, `${collection} index`);
    if (
      !Array.isArray(parsed) ||
      !parsed.every((id) => typeof id === "string")
    ) {
      throw new DbError(
        `Invalid Cloudflare KV index for ${collection}`,
        "QUERY_ERROR",
      );
    }
    return parsed;
  };

  const readListedRecordIds = async (
    collection: string,
  ): Promise<string[] | null> => {
    if (!namespace.list) {
      return null;
    }

    const prefix = collectionRecordPrefix(collection);
    const indexKey = collectionKey(collection);
    assertKvKeySize(prefix, `${collection} records prefix`);
    assertKvKeySize(indexKey, `${collection} index`);
    const ids = new Set<string>();
    const seenCursors = new Set<string>();
    let cursor: string | undefined;

    try {
      do {
        const page = assertListResult(
          await namespace.list({ cursor, prefix }),
          collection,
        );

        for (const key of page.keys) {
          if (typeof key.name !== "string" || !key.name.startsWith(prefix)) {
            throw new DbError(
              `Invalid Cloudflare KV list key for ${collection}`,
              "QUERY_ERROR",
            );
          }
          if (key.name === indexKey) {
            continue;
          }

          try {
            ids.add(decodeURIComponent(key.name.slice(prefix.length)));
          } catch (error) {
            throw new DbError(
              `Invalid Cloudflare KV encoded record key for ${collection}`,
              "QUERY_ERROR",
              error,
            );
          }
        }

        if (page.list_complete) {
          cursor = undefined;
        } else {
          const nextCursor = page.cursor;
          if (!nextCursor) {
            throw new DbError(
              `Cloudflare KV list page for ${collection} is incomplete without a cursor`,
              "QUERY_ERROR",
            );
          }
          if (seenCursors.has(nextCursor)) {
            throw new DbError(
              `Cloudflare KV list cursor repeated for ${collection}`,
              "QUERY_ERROR",
            );
          }
          seenCursors.add(nextCursor);
          cursor = nextCursor;
        }
      } while (cursor);
    } catch (error) {
      if (error instanceof DbError) {
        throw error;
      }
      throw new DbError(
        `Cloudflare KV list failed for ${collection} records`,
        "CONNECTION_ERROR",
        error,
      );
    }

    return [...ids];
  };

  const readRecordIds = async (collection: string): Promise<string[]> =>
    (await readListedRecordIds(collection)) ?? readIndex(collection);

  const readUniqueRecordIds = async (
    collection: string,
  ): Promise<{ ids: string[]; repaired: boolean }> => {
    const ids = await readRecordIds(collection);
    const uniqueIds = [...new Set(ids)];
    return { ids: uniqueIds, repaired: uniqueIds.length !== ids.length };
  };

  const withCollectionIndexMutation = async <T>(
    collection: string,
    operation: () => Promise<T>,
  ): Promise<T> => {
    const previous = indexMutationLocks.get(collection) ?? Promise.resolve();
    let releaseCurrent!: () => void;
    const current = new Promise<void>((resolve) => {
      releaseCurrent = resolve;
    });
    const next = previous.catch(() => undefined).then(() => current);
    indexMutationLocks.set(collection, next);

    await previous.catch(() => undefined);
    try {
      return await operation();
    } finally {
      releaseCurrent();
      if (indexMutationLocks.get(collection) === next) {
        indexMutationLocks.delete(collection);
      }
    }
  };

  const writeIndexUnlocked = async (
    collection: string,
    ids: string[],
  ): Promise<void> => {
    await kvPut(
      collectionKey(collection),
      stringifyKvJson([...new Set(ids)], `${collection} index`),
      `${collection} index`,
      indexPutOptions,
    );
  };

  const writeIndex = async (collection: string, ids: string[]): Promise<void> =>
    withCollectionIndexMutation(collection, () =>
      writeIndexUnlocked(collection, ids),
    );

  const readRecord = async (
    collection: string,
    id: string,
  ): Promise<KvRecord | null> => {
    const raw = await kvGet(
      recordKey(collection, id),
      `${collection}:${id} record`,
    );
    if (!raw) {
      return null;
    }
    const parsed = parseKvJson(raw, `${collection}:${id} record`);
    if (!isRecord(parsed) || typeof parsed.id !== "string") {
      throw new DbError(
        `Invalid Cloudflare KV record for ${collection}:${id}`,
        "QUERY_ERROR",
      );
    }
    if (parsed.id !== id) {
      throw new DbError(
        `Cloudflare KV record identity mismatch for ${collection}:${id}`,
        "QUERY_ERROR",
      );
    }
    return parsed as KvRecord;
  };

  const writeRecord = async (
    collection: string,
    id: string,
    record: KvRecord,
  ): Promise<void> => {
    await kvPut(
      recordKey(collection, id),
      stringifyKvJson(record, `${collection}:${id} record`),
      `${collection}:${id} record`,
      recordPutOptions,
    );
  };

  const readRecords = async (
    collection: string,
    options?: ListOptions,
    scan?: { maxRecords: number; operation: "count" | "query" },
  ): Promise<KvRecord[]> => {
    const { ids: uniqueIds, repaired: indexWasDeduped } =
      await readUniqueRecordIds(collection);

    if (scan && uniqueIds.length > scan.maxRecords) {
      throw new AdapterUnsupportedError(
        `Cloudflare KV ${scan.operation} scan would read ${uniqueIds.length} records, exceeding maxScanRecords ${scan.maxRecords}`,
      );
    }

    // For ordered listings we still need the full live set to sort across, so
    // skip the streaming pagination and let downstream sortRecords/pageRecords
    // operate on the live records collected here.
    const usesOrdering = Boolean(options?.orderBy);
    const offset = options?.offset ?? 0;
    const limit = options?.limit;

    // Short-circuit zero-limit unordered lists before reading any record
    // payloads — there is no live page to fill and the existing test suite
    // (and KV cost model) expects this to avoid record reads entirely.
    if (!usesOrdering && limit === 0) {
      return [];
    }

    const records: KvRecord[] = [];
    const liveIds: string[] = [];
    let scannedAll = true;
    let liveSeen = 0;

    for (const id of uniqueIds) {
      const record = await readRecord(collection, id);
      const isLive = record !== null;
      if (isLive) {
        liveIds.push(id);
        if (
          usesOrdering ||
          (liveSeen >= offset &&
            (limit === undefined || records.length < limit))
        ) {
          records.push(record as KvRecord);
        }
        liveSeen += 1;
      }

      // Once we have collected enough live records for an unordered limited
      // page we can stop scanning the rest of the index. We still need to
      // continue past stale ids before we have the live page filled so that
      // limit:1 lists do not return [] when the first index entry is stale.
      if (
        !usesOrdering &&
        limit !== undefined &&
        records.length >= limit &&
        liveSeen >= offset + limit
      ) {
        scannedAll = false;
        break;
      }
    }

    // Repair the index whenever we touched every id (so we observed every
    // stale entry) and the live set differs from the stored index, or the
    // index needed deduping in the first place.
    if (
      scannedAll &&
      (liveIds.length !== uniqueIds.length || indexWasDeduped)
    ) {
      await writeIndex(collection, liveIds);
    } else if (indexWasDeduped) {
      await writeIndex(collection, uniqueIds);
    }

    return records;
  };

  const countIndexedRecords = async (collection: string): Promise<number> => {
    const { ids, repaired } = await readUniqueRecordIds(collection);
    if (repaired) {
      await writeIndex(collection, ids);
    }
    return ids.length;
  };

  return {
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ) {
      const id = assertOperationKeyPart(idFactory(), "id");
      assertJsonRecordPayload(data, `${collection} create`);

      return withCollectionIndexMutation(collection, async () => {
        if (await readRecord(collection, id)) {
          throw new DbError(
            `Cloudflare KV record already exists for ${collection}:${id}`,
            "CONSTRAINT_VIOLATION",
          );
        }

        const record = { ...data, id } as KvRecord;
        const ids = await readIndex(collection);
        await writeRecord(collection, id, record);
        try {
          await writeIndexUnlocked(collection, [...ids, id]);
        } catch (error) {
          try {
            await kvDelete(
              recordKey(collection, id),
              `${collection}:${id} rollback record`,
            );
          } catch (rollbackError) {
            throw new DbError(
              `Cloudflare KV create failed for ${collection}:${id}; rollback delete also failed and may leave an orphaned record`,
              "CONNECTION_ERROR",
              { indexWriteError: error, rollbackError },
            );
          }
          throw error;
        }
        return record as T & { id: string };
      });
    },
    async get<T>(collection: string, id: string) {
      return (await readRecord(collection, id)) as T | null;
    },
    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ) {
      assertJsonRecordPayload(data, `${collection}:${id} update`);
      return withCollectionIndexMutation(collection, async () => {
        const current = await readRecord(collection, id);
        if (!current) {
          return null;
        }
        const next = { ...current, ...data, id } as KvRecord;
        const ids = await readIndex(collection);
        if (!ids.includes(id)) {
          await writeIndexUnlocked(collection, [...ids, id]);
        }
        await writeRecord(collection, id, next);
        return next as T;
      });
    },
    async delete(collection: string, id: string) {
      return withCollectionIndexMutation(collection, async () => {
        const current = await readRecord(collection, id);
        if (!current) {
          return false;
        }
        // Mutate the index first, then delete the record. If the record delete
        // fails the caller sees a failure but the index has been updated to no
        // longer reference the (still-present) record — attempt to roll the
        // index back so the collection is not corrupted. The reverse order
        // (record first, then index) leaves the index pointing at a missing
        // record when the index write fails, which is far worse for
        // count()/list() consumers that scan the index.
        const previousIds = await readIndex(collection);
        const nextIds = previousIds.filter((candidate) => candidate !== id);
        await writeIndexUnlocked(collection, nextIds);
        try {
          await kvDelete(
            recordKey(collection, id),
            `${collection}:${id} record`,
          );
        } catch (error) {
          // Best-effort rollback: restore the prior index so callers can retry
          // the delete without losing track of the still-present record.
          try {
            await writeIndexUnlocked(collection, previousIds);
          } catch {
            // Swallow rollback errors — surface the original delete failure.
          }
          throw error;
        }
        return true;
      });
    },
    async list<T>(collection: string, options?: ListOptions) {
      assertListOptions(options);
      const records = await readRecords(collection, options);
      if (!options?.orderBy) {
        return records as T[];
      }
      return pageRecords(sortRecords(records, options), options) as T[];
    },
    async query<T>(collection: string, filter: QueryFilter) {
      const checkedFilter = assertQueryFilter(filter, "query");
      return (
        await readRecords(collection, undefined, {
          maxRecords: maxScanRecords,
          operation: "query",
        })
      ).filter((record) => matchesFilter(record, checkedFilter)) as T[];
    },
    async count(collection: string, filter?: QueryFilter) {
      const checkedFilter = filter
        ? assertQueryFilter(filter, "count")
        : undefined;
      if (!checkedFilter || checkedFilter.length === 0) {
        // Unfiltered count: read ids only (namespace.list when available, or
        // the collection index fallback) and skip record payload fetches.
        // `namespace.list` reflects post-TTL state, so this stays accurate when
        // the runtime supports listing. When only the fallback index is
        // available and records have TTL, scan live records so expired payloads
        // are pruned instead of counted forever.
        if (defaultTtlSeconds !== undefined && !namespace.list) {
          return (
            await readRecords(collection, undefined, {
              maxRecords: maxScanRecords,
              operation: "count",
            })
          ).length;
        }
        return countIndexedRecords(collection);
      }
      const records = await readRecords(collection, undefined, {
        maxRecords: maxScanRecords,
        operation: "count",
      });
      return records.filter((record) => matchesFilter(record, checkedFilter))
        .length;
    },
    async join() {
      return unsupportedRelationalOperation("join");
    },
    async sql() {
      return unsupportedRelationalOperation("sql");
    },
    async transaction() {
      return unsupportedRelationalOperation("transaction");
    },
  };
}

/**
 * Normalizes Cloudflare KV adapter input into a shared DB adapter instance.
 *
 * @param config Existing DB adapter, adapter binding, or KV adapter options.
 * @returns A DB adapter suitable for the shared adapters contract.
 */
export function withCloudflareKvDb(
  config: CloudflareKvDbAdapterInput,
): DbAdapter {
  const adapter =
    isRecord(config) && "adapter" in config ? config.adapter : config;
  if (isCloudflareKvDbAdapterOptions(adapter)) {
    return createCloudflareKvDbAdapter(adapter);
  }

  assertDbAdapter(adapter);
  return adapter;
}

/** @doc SDK-free domain contract interfaces exposed consistently across launch DB backend subpaths. */
export type {
  AuthProviderAdapter,
  CacheAdapter,
  DeployAdapter,
  EventsAdapter,
  PaymentsProviderAdapter,
  QueueAdapter,
  StorageAdapter,
} from "@geenius/adapters";
/** @doc SDK-free domain Zod schemas and error classes exposed by this backend contract subpath. */
export {
  AuthContractError,
  AuthIdentitySchema,
  AuthSessionContractSchema,
  CacheContractError,
  CacheEntrySchema,
  DeployContractError,
  DeploymentResultSchema,
  DeploymentTargetSchema,
  EventEnvelopeSchema,
  EventsContractError,
  PaymentCheckoutInputSchema,
  PaymentPlanContractSchema,
  PaymentSubscriptionContractSchema,
  PaymentsContractError,
  QueueContractError,
  QueueJobSchema,
  StorageContractError,
  StorageObjectSchema,
} from "@geenius/adapters";
